import {
    doc,
    getDoc,
    setDoc,
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from '../firebase';

// Default data structure
const defaultSiteData = {
    hero: {
        title: "Savana Ashlea",
        subtitle: "Artist • Photographer • Yoga Instructor",
        backgroundImage: "",
        backgroundVideo: "",
        backgroundImagePath: ""
    },
    about: {
        title: "About Me",
        content: "Welcome to my creative world where art, photography, and wellness converge. I'm passionate about capturing life's beautiful moments, creating meaningful art, and helping others find peace through yoga practice.\n\nWith years of experience in both artistic expression and mindful movement, I bring a unique perspective to every project and session. Whether you're looking for stunning photography, custom artwork, or a transformative yoga experience, I'm here to help you discover beauty and balance in your life.",
        image: "",
        imagePath: ""
    },
    photography: {
        title: "Photography Portfolio",
        photos: [],
        photoshoots: [] // Add this
    },
    yoga: {
        title: "Yoga Services",
        services: []
    },
    art: {
        title: "Art Commissions",
        pieces: []
    },
    contact: {
        email: "savana@example.com",
        phone: "(555) 123-4567"
    }
};

// Main site data functions
export const loadSiteData = async () => {
    try {
        const docRef = doc(db, 'siteData', 'main');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return mergeWithDefaults(data, defaultSiteData);
        } else {
            // Initialize with default data
            await setDoc(docRef, defaultSiteData);
            return defaultSiteData;
        }
    } catch (error) {
        console.error('Failed to load site data:', error);
        return defaultSiteData;
    }
};

export const saveSiteData = async (section, data) => {
    try {
        const docRef = doc(db, 'siteData', 'main');

        // Use setDoc with merge option to ensure the document exists
        await setDoc(docRef, {
            [section]: data
        }, { merge: true });

        console.log(`Successfully saved ${section} data:`, data);
        return data;
    } catch (error) {
        console.error('Failed to save site data:', error);
        throw error;
    }
};

// Image upload function
export const uploadImage = async (file, path) => {
    try {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        const imageRef = ref(storage, `${path}/${fileName}`);

        const snapshot = await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        return {
            url: downloadURL,
            path: snapshot.ref.fullPath,
            name: fileName
        };
    } catch (error) {
        console.error('Failed to upload image:', error);
        throw error;
    }
};

// Delete image function
export const deleteImage = async (imagePath) => {
    try {
        if (imagePath) {
            const imageRef = ref(storage, imagePath);
            await deleteObject(imageRef);
        }
    } catch (error) {
        console.error('Failed to delete image:', error);
        // Don't throw error for image deletion failures
    }
};

// Photography functions
export const addPhoto = async (photo) => {
    try {
        const docRef = await addDoc(collection(db, 'photos'), {
            ...photo,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error('Failed to add photo:', error);
        throw error;
    }
};

export const updatePhoto = async (id, photo) => {
    try {
        const docRef = doc(db, 'photos', id);
        await updateDoc(docRef, {
            ...photo,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Failed to update photo:', error);
        throw error;
    }
};

export const deletePhoto = async (id, imagePath) => {
    try {
        await deleteDoc(doc(db, 'photos', id));
        if (imagePath) {
            await deleteImage(imagePath);
        }
    } catch (error) {
        console.error('Failed to delete photo:', error);
        throw error;
    }
};

export const loadPhotos = async () => {
    try {
        const q = query(collection(db, 'photos'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Failed to load photos:', error);
        return [];
    }
};

// Photoshoot functions
export const addPhotoshoot = async (photoshoot) => {
    try {
        const docRef = await addDoc(collection(db, 'photoshoots'), {
            ...photoshoot,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error('Failed to add photoshoot:', error);
        throw error;
    }
};

export const updatePhotoshoot = async (id, photoshoot) => {
    try {
        const docRef = doc(db, 'photoshoots', id);
        await updateDoc(docRef, {
            ...photoshoot,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Failed to update photoshoot:', error);
        throw error;
    }
};

export const deletePhotoshoot = async (id, photos = []) => {
    try {
        await deleteDoc(doc(db, 'photoshoots', id));

        // Delete all images in the photoshoot
        for (const photo of photos) {
            if (photo.imagePath) {
                await deleteImage(photo.imagePath);
            }
        }
    } catch (error) {
        console.error('Failed to delete photoshoot:', error);
        throw error;
    }
};

export const loadPhotoshoots = async () => {
    try {
        const q = query(collection(db, 'photoshoots'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        // Load each photoshoot with its photo count
        const photoshootsWithCounts = await Promise.all(
            querySnapshot.docs.map(async (doc) => {
                const photoshootData = {
                    id: doc.id,
                    ...doc.data()
                };

                // Get the actual photo count from the subcollection
                const photosQuery = query(collection(db, 'photoshoots', doc.id, 'photos'));
                const photosSnapshot = await getDocs(photosQuery);
                photoshootData.photoCount = photosSnapshot.size;

                return photoshootData;
            })
        );

        return photoshootsWithCounts;
    } catch (error) {
        console.error('Failed to load photoshoots:', error);
        return [];
    }
};

// Individual photo functions within photoshoots
export const addPhotoToShoot = async (photoshootId, photo) => {
    try {
        const docRef = await addDoc(collection(db, 'photoshoots', photoshootId, 'photos'), {
            ...photo,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error('Failed to add photo to shoot:', error);
        throw error;
    }
};

export const loadPhotoshootPhotos = async (photoshootId) => {
    try {
        const q = query(
            collection(db, 'photoshoots', photoshootId, 'photos'),
            orderBy('createdAt', 'asc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Failed to load photoshoot photos:', error);
        return [];
    }
};

// Yoga service functions
export const addService = async (service) => {
    try {
        const docRef = await addDoc(collection(db, 'yogaServices'), {
            ...service,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error('Failed to add service:', error);
        throw error;
    }
};

export const updateService = async (id, service) => {
    try {
        const docRef = doc(db, 'yogaServices', id);
        await updateDoc(docRef, {
            ...service,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Failed to update service:', error);
        throw error;
    }
};

export const deleteService = async (id, imagePath) => {
    try {
        await deleteDoc(doc(db, 'yogaServices', id));
        if (imagePath) {
            await deleteImage(imagePath);
        }
    } catch (error) {
        console.error('Failed to delete service:', error);
        throw error;
    }
};

export const loadServices = async () => {
    try {
        const q = query(collection(db, 'yogaServices'), orderBy('createdAt', 'asc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Failed to load services:', error);
        return [];
    }
};

// Art piece functions
export const addArtPiece = async (piece) => {
    try {
        const docRef = await addDoc(collection(db, 'artPieces'), {
            ...piece,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error('Failed to add art piece:', error);
        throw error;
    }
};

export const updateArtPiece = async (id, piece) => {
    try {
        const docRef = doc(db, 'artPieces', id);
        await updateDoc(docRef, {
            ...piece,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Failed to update art piece:', error);
        throw error;
    }
};

export const deleteArtPiece = async (id, imagePath) => {
    try {
        await deleteDoc(doc(db, 'artPieces', id));
        if (imagePath) {
            await deleteImage(imagePath);
        }
    } catch (error) {
        console.error('Failed to delete art piece:', error);
        throw error;
    }
};

export const loadArtPieces = async () => {
    try {
        const q = query(collection(db, 'artPieces'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Failed to load art pieces:', error);
        return [];
    }
};

// Helper function to merge data with defaults
const mergeWithDefaults = (data, defaults) => {
    const result = { ...defaults };

    Object.keys(data).forEach(key => {
        if (typeof data[key] === 'object' && !Array.isArray(data[key]) && data[key] !== null) {
            result[key] = { ...defaults[key], ...data[key] };
        } else {
            result[key] = data[key];
        }
    });

    return result;
};

